/**
 * Canonical account-deletion disclosure copy.
 * Source: docs/compliance/brushfire-sp-account-deletion-disclosure.md
 * Update only after Legal sign-off (set status + LEGAL_SIGNOFF_DATE).
 */

export const DELETION_DISCLOSURE_VERSION = '1.0.0'
export const DELETION_DISCLOSURE_STATUS = 'approved' as const
export const LEGAL_SIGNOFF_DATE = '2026-06-17'

export const DEFAULT_GRACE_DAYS = 30

/** Retention periods — Legal v1.0 (see compliance doc). */
export const RETENTION_DISCLOSURE = {
  rewardPayoutRecordsYears: 7,
  kycFraudOutcomesYears: 5,
  auditLogYears: 7,
} as const

export const DELETION_COPY = {
  pageTitle: 'Delete your account',
  intro:
    'This page permanently deletes your BrushFire Signal Provider account. You do not need the mobile app installed to use this form.',
  deletedHeading: 'What we delete',
  deletedItems: [
    'Your onboarding profile (demographics, preferences, contact details)',
    'Account credentials and active sessions',
    'Personally identifying survey response content where stored',
    'Push notification targets linked to your account (FCM)',
  ],
  retainedHeading: 'What we may retain (disclosed)',
  balanceHeading: 'Reward balance',
  balanceBody:
    'Any unredeemed reward balance at the time of confirmation is forfeited per our program terms. Pending payouts must reach a terminal state before purge completes.',
  timelineHeading: 'Timeline',
  irreversibilityNote:
    'After the grace period ends, deletion is irreversible and cannot be undone.',
  emailHint:
    'If an account exists for this email, you will receive a verification code. We never confirm whether an address is registered.',
  emailNotSent:
    'No verification code was sent. This email may not belong to an active Signal Provider account, or deletion may already be scheduled. Sign in on the web or mobile app to cancel an in-progress deletion during the grace period.',
  confirmCheckbox: (graceDays: number) =>
    `I understand this permanently schedules deletion of my BrushFire account after the ${graceDays}-day grace period, that unredeemed balance may be forfeited, and that this cannot be undone after the grace period ends.`,
  successScheduled: (graceDays: number) =>
    `Your account deletion has been scheduled. You have ${graceDays} days to cancel by signing in on the web or mobile app. After that, your account and personal data will be permanently deleted subject to the retention disclosures above.`,
  pendingBannerTitle: 'Account deletion scheduled',
  pendingBannerBody: (daysRemaining: number | null, scheduledLabel: string | null) => {
    const dayPart = formatDeletionDaysRemaining(daysRemaining)
    const purgePart = scheduledLabel ? ` Scheduled completion: ${scheduledLabel}.` : ''
    return `Your account is scheduled for deletion (${dayPart}).${purgePart} Cancel below to keep your account. Forfeited reward balance is not restored.`
  },
  cancelDeletionCta: 'Cancel account deletion',
  cancelDeletionSuccess: 'Account deletion cancelled. Your account is active again.',
  cancelDeletionError: 'Unable to cancel account deletion. Please try again or contact support.',
  successFooter:
    'You may close this page. A confirmation email will be sent if your request was accepted.',
  legalPendingBanner:
    'Retention periods shown are draft values pending Legal sign-off.',
} as const

/** Formats grace-period countdown for pending-deletion banner copy. */
export function formatDeletionDaysRemaining(daysRemaining: number | null): string {
  if (daysRemaining === null) {
    return 'Grace period active'
  }
  if (daysRemaining === 1) {
    return '1 day remaining'
  }
  return `${daysRemaining} days remaining`
}

/** Hosted deletion URLs per environment (Google Play field). */
export const PUBLIC_DELETION_URLS = {
  dev: 'https://provider.brushfire-dev.bridgebss.com/brushfire/delete-account',
  qa: 'https://provider.brushfire-qa.bridgebss.com/brushfire/delete-account',
  prod: 'https://provider.brushfire.bridgebss.com/brushfire/delete-account',
} as const
